import styled from '@emotion/styled';
import { ChevronLeft, Copy, ExternalLink, RotateCcw, Save } from 'lucide-react';
import { useFabricEditorOptional } from 'modules/fabric-editor';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { selectActiveTemplate } from 'modules/manager/store';
import { useResetTemplate, useSaveTemplate } from 'modules/manager/ui/hooks';
import useDuplicateTemplateModal from 'modules/manager/ui/hooks/modals/use-duplicate-template-modal';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useIsLocal from 'shared/hooks/use-is-local';
import { useAppSelector } from 'store/hooks';
import { editorAccent } from '../editor-accent';
import { buildDevRenderLink } from './dev-render-link';

const EditorHeader: React.FC = () => {
  const navigate = useNavigate();
  const template = useAppSelector(selectActiveTemplate);
  const editor = useFabricEditorOptional();
  const { save, isDirty, isSaving, canSave, canEdit } = useSaveTemplate(editor);
  const { reset, canReset } = useResetTemplate(editor);
  const isLocal = useIsLocal();

  const handleBack = useCallback(() => {
    navigate(Pages.MEDIA_MANAGER.INDEX);
  }, [navigate]);

  // From the editor the user is already editing, so open the copy straight away
  // instead of bouncing through the list.
  const onDuplicated = useCallback((copy: MediaManagerTemplate) => navigate(Pages.MEDIA_MANAGER.builders.editor(copy.id)), [navigate]);

  const { modal: duplicateModal, open: openDuplicateModal, canDuplicate, isDuplicating } = useDuplicateTemplateModal({ onDuplicated });

  const devRenderLink = isLocal && template ? buildDevRenderLink(template) : null;
  // A regular user opened a global preset: it is read-only until they duplicate it.
  const isReadOnlyPreset = template != null && !canEdit;

  return (
    <Root>
      <BackButton type="button" onClick={handleBack} aria-label="Назад">
        <ChevronLeft size={18} />
      </BackButton>
      <Title>
        <span className="font-semibold">{template?.name ?? '—'}</span>
        {isReadOnlyPreset ? (
          <ReadOnlyBadge>Пресет — дублируйте, чтобы редактировать</ReadOnlyBadge>
        ) : (
          isDirty && <UnsavedBadge>Несохранённые изменения</UnsavedBadge>
        )}
      </Title>
      <Spacer />
      {devRenderLink && (
        <DevRenderLink
          href={devRenderLink}
          target="_blank"
          rel="noreferrer"
          title={`Только для локальной разработки. Открыть страницу рендера с тестовыми параметрами:\n${devRenderLink}\n\nСтраница читает шаблон из базы — несохранённые изменения в неё не попадут.`}
        >
          <ExternalLink size={16} />
          <span>Рендер</span>
        </DevRenderLink>
      )}
      {isReadOnlyPreset ? (
        <SaveButton
          type="button"
          onClick={() => template && openDuplicateModal(template)}
          disabled={!canDuplicate || isDuplicating}
          aria-label="Дублировать"
          title={canDuplicate ? undefined : 'Нет активного турнира для копии'}
        >
          <Copy size={16} />
          <span>{isDuplicating ? 'Копирование…' : 'Дублировать'}</span>
        </SaveButton>
      ) : (
        <>
          <ResetButton
            type="button"
            onClick={reset}
            disabled={!canReset || isSaving}
            aria-label="Сбросить изменения"
            title="Вернуть шаблон к последнему сохранённому состоянию. Сам сброс отменяется через Ctrl+Z."
          >
            <RotateCcw size={16} />
            <span>Сбросить</span>
          </ResetButton>
          <SaveButton type="button" onClick={() => void save()} disabled={!canSave} aria-label="Сохранить">
            <Save size={16} />
            <span>{isSaving ? 'Сохранение…' : 'Сохранить'}</span>
          </SaveButton>
        </>
      )}
      {duplicateModal}
    </Root>
  );
};

export default EditorHeader;

const Root = styled.header`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  height: 56px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  color: #374151;
  &:hover {
    background: #f3f4f6;
  }
`;

const Title = styled.div`
  font-size: 14px;
  color: #111827;
  display: flex;
  align-items: center;
`;

const UnsavedBadge = styled.span`
  margin-left: 12px;
  padding: 2px 8px;
  border-radius: 9999px;
  background: #fef3c7;
  color: #92400e;
  font-size: 12px;
  font-weight: 500;
`;

const ReadOnlyBadge = styled.span`
  margin-left: 12px;
  padding: 2px 8px;
  border-radius: 9999px;
  background: ${editorAccent.soft};
  color: ${editorAccent.ink};
  font-size: 12px;
  font-weight: 500;
`;

const Spacer = styled.div`
  flex: 1;
`;

/** Local-only — see dev-render-link.ts. Styled as a secondary action so it never competes with Save. */
const DevRenderLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  color: #334155;
  font-size: 14px;
  font-weight: 500;
  &:hover {
    background: #f1f5f9;
  }
`;

// Discarding edits is undoable (Ctrl+Z), so this stays a quiet outline button
// rather than a red destructive one — and it must not compete with Save.
const ResetButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  color: #334155;
  font-size: 14px;
  font-weight: 500;
  &:hover:not(:disabled) {
    background: #f1f5f9;
  }
  &:disabled {
    color: #cbd5e1;
    border-color: #e2e8f0;
    cursor: not-allowed;
  }
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  background: ${editorAccent.solid};
  color: ${editorAccent.onSolid};
  font-size: 14px;
  font-weight: 500;
  &:hover:not(:disabled) {
    background: ${editorAccent.solidHover};
  }
  &:disabled {
    background: #e2e8f0;
    color: #94a3b8;
    cursor: not-allowed;
  }
`;
